import React from 'react';
import _ from 'lodash';

import {
  AcceptReturnAuthorizationModal,
  ApproveReturnAuthorizationModal,
  NetSuiteRecordChip,
  PermissionWall,
  ReceiveReturnAuthorizationModal,
  currencyUtil,
  routeDirectory,
  tableUtil
} from 'modules';
import {
  Button as MuiButton,
  Link as MuiLink,
  styled
} from '@mui/material';
import {
  LoadingOverlay,
  Table,
  modalUtil
} from 'iron-forge-react-modules';

import { DateTime } from 'luxon';
import { QueryReturnAuthorizationsFilterInput } from 'generated/graphql';
import * as ShippingStatusChip from './ShippingStatusChip';
import * as dataService from './dataService';
import * as types from './types';



const Button = styled(MuiButton)(() => ({
  '&:hover': {
    backgroundColor: '#E5EFFD'
  },
  backgroundColor: '#E5EFFD',
  color: '#3A3B3D'
}));

const Link = styled(MuiLink)({
  fontSize: 12
});

const TableComponent = Table.genTableComponent<types.IColumnId>({
  tableHeaderCellStyle: {
    textAlign: 'center'
  },
  tableBodyCellStyle: {
    border: 'none'
  }
});



export interface IProps {
  filter?: QueryReturnAuthorizationsFilterInput;
  columnIdsToRender: types.IColumnId[];
}

export const FC: React.FC<IProps> = props => {
  const {
    queryTuple,
    pageInfo,
    fnQueryPage,
    fnQuery
  } = dataService.useQuery(props.filter);
  const [
    generateReturnLabel,
    generateReturnLabelTuple
  ] = dataService.useGenerateReturnLabel(props.filter?.storeId || undefined);
  const {
    modalType2TargetId,
    setModalIsOpen
  } = modalUtil.useModalTrackers([
    'receiveItems',
    'approveReturnAuthorization',
    'acceptReturnAuthorization'
  ]);

  const handleClickReceiveItems = React.useCallback(
    (returnAuthorizationId: string) => {
      setModalIsOpen('receiveItems', returnAuthorizationId);
    },
    [setModalIsOpen]
  );

  const handleClickApproveDeny = React.useCallback(
    (returnAuthorizationId: string) => {
      setModalIsOpen('approveReturnAuthorization', returnAuthorizationId);
    },
    [setModalIsOpen]
  );

  const handleClickAccept = React.useCallback(
    (returnAuthorizationId: string) => {
      setModalIsOpen('acceptReturnAuthorization', returnAuthorizationId);
    },
    [setModalIsOpen]
  );

  const handleClickShowLabel = React.useCallback(
    (labelUrl: string) => {
      window.open(labelUrl, '_blank');
    },
    []
  );

  const handleClickGenerateLabel = React.useCallback(
    async (returnAuthorizationId: string) => {
      if (window.confirm("Are you sure you want to generate a return label? This will purchase a return label via EasyPost")) {
        await generateReturnLabel(returnAuthorizationId);
        queryTuple.refetch?.();
      }
    },
    [
      queryTuple,
      generateReturnLabel
    ]
  );

  const handleChangeSortOptions = React.useCallback(
    (sortOptions: { columnId: types.IColumnId | ''; direction: 'ascending' | 'descending'; }) => {
      if (!sortOptions.columnId) {
        return;
      }

      fnQuery({
        sort: {
          columnId: sortOptions.columnId,
          direction: sortOptions.direction
        }
      });
    },
    [fnQuery]
  );

  const initSortOptions = React.useMemo(
    () => {
      const isPendingApprovalOnly = _.isEqual(
        props.filter?.authorizationApprovalStatus,
        ['pending']
      );

      return {
        columnId: (isPendingApprovalOnly ? 'requestDate' : 'approvalDate') as types.IColumnId,
        direction: 'descending' as const
      };
    },
    [props.filter?.authorizationApprovalStatus]
  );



  const loading = queryTuple.loading || generateReturnLabelTuple.loading;

  const rows = React.useMemo(
    () => _.map(queryTuple.data.returnAuthorizations, (returnAuthorization): types.IRow => ({
      id: returnAuthorization.id,
      columnId2Value: {
        approvalDate: {
          type: 'string',
          sortableValue: returnAuthorization.approval?.createdDate.toMillis() ?? 0,
          label: __formatDate(returnAuthorization.approval?.createdDate)
        },
        requestDate: {
          type: 'string',
          sortableValue: returnAuthorization.returnDate.toMillis(),
          label: __formatDate(returnAuthorization.returnDate)
        },
        orderNumber: {
          type: 'component',
          sortableValue: returnAuthorization.order.orderNumber,
          label: (
            <Link
              href={routeDirectory.partner.storeDetail.path(returnAuthorization.store.id, returnAuthorization.order.id)}
              target="_blank">
              {returnAuthorization.order.orderNumber}
            </Link>
          )
        },
        orderExternalId: {
          type: 'component',
          sortableValue: returnAuthorization.order.externalId,
          label: (
            <Link
              href={returnAuthorization.order.urlToExternalOrder}
              target="_blank">
              {returnAuthorization.order.externalId}
            </Link>
          )
        },
        customerEmail: {
          type: 'string',
          sortableValue: returnAuthorization.customer.email.toLowerCase(),
          label: returnAuthorization.customer.email
        },
        acceptDenyRequest: {
          type: 'component',
          sortableValue: _.isNil(returnAuthorization.acceptance) ? 0 : returnAuthorization.acceptance.approvalStatus === 'denied' ? 1 : 2,
          label: _.isNil(returnAuthorization.acceptance) ? (
            <Button
              disableElevation
              variant="contained"
              onClick={_.partial(handleClickAccept, returnAuthorization.id)}>
              Process Request
            </Button>
          ) : (
            <div />
          )
        },
        approveDenyRequest: {
          type: 'component',
          sortableValue: returnAuthorization.approval ? 0 : 1,
          label: (
            <Button
              disableElevation
              variant="contained"
              onClick={_.partial(handleClickApproveDeny, returnAuthorization.id)}>
              Process Request
            </Button>
          )
        },
        customerFullName: {
          type: 'string',
          sortableValue: returnAuthorization.customer.fullName.toLowerCase(),
          label: returnAuthorization.customer.fullName
        },
        customerPhoneNumber: {
          type: 'string',
          sortableValue: returnAuthorization.customer.phoneNumber,
          label: returnAuthorization.customer.phoneNumber
        },
        itemsFulfilledCount: {
          type: 'string',
          sortableValue: returnAuthorization.itemsFulfilledCount,
          label: returnAuthorization.itemsFulfilledCount.toString()
        },
        itemsToBeReturnedCount: {
          type: 'string',
          sortableValue: returnAuthorization.itemsToBeReturnedCount,
          label: returnAuthorization.itemsToBeReturnedCount.toString()
        },
        netSuiteSalesOrderRecord: {
          type: 'component',
          sortableValue: returnAuthorization.order.netSuiteWriteStatus.type,
          label: <NetSuiteRecordChip.FC writeStatus={returnAuthorization.order.netSuiteWriteStatus} />
        },
        orderAmount: {
          type: 'string',
          sortableValue: returnAuthorization.order.totalAmountCents.toNumber(),
          label: currencyUtil.convertBigCentsToDollars(returnAuthorization.order.totalAmountCents)
        },
        orderDate: {
          type: 'string',
          sortableValue: returnAuthorization.order.orderDate.toMillis(),
          label: __formatDate(returnAuthorization.order.orderDate)
        },
        storeName: {
          type: 'component',
          sortableValue: returnAuthorization.store.name.toLowerCase(),
          label: (
            <Link
              href={routeDirectory.partner.storeDetail.path(returnAuthorization.store.id)}
              target="_blank">
              {returnAuthorization.store.name}
            </Link>
          )
        },
        shippingLabel: {
          type: 'component',
          sortableValue: returnAuthorization.shipment?.labelUrl ? 1 : 0,
          label: returnAuthorization.shipment?.labelUrl ? (
            <PermissionWall.FC
              storeId={returnAuthorization.store.id}
              requiresPermissions={['returnAuthorization_downloadLabel']}>
              <Button
                disableElevation
                size="small"
                disabled={loading}
                variant="contained"
                onClick={() => handleClickShowLabel(returnAuthorization.shipment?.labelUrl ?? '')}>
                Show Label
              </Button>
            </PermissionWall.FC>

          ) : (
            <PermissionWall.FC
              storeId={returnAuthorization.store.id}
              requiresPermissions={['returnAuthorization_generateLabel']}>
              <Button
                disabled={loading}
                variant="contained"
                disableElevation
                size="small"
                onClick={() => handleClickGenerateLabel(returnAuthorization.id)}>
                Generate Label
              </Button>
            </PermissionWall.FC>
          )
        },
        receiveItems: {
          type: 'component',
          sortableValue: 0,
          label: (
            <Button
              disableElevation
              size="small"
              variant="contained"
              onClick={_.partial(handleClickReceiveItems, returnAuthorization.id)}>
              Receive Items
            </Button>)
        },
        returnNumber: {
          type: 'component',
          sortableValue: returnAuthorization.returnNumber,
          label: (
            <Link
              href={routeDirectory.admin.returnAuthorizations.detail.path(returnAuthorization.id)}
              target="_blank">
              {returnAuthorization.returnNumber}
            </Link>
          )
        },

        netSuiteReturnAuthorizationRecord: {
          type: 'component',
          sortableValue: returnAuthorization.returnAuthorizationNetSuiteWriteStatus.type,
          label: <NetSuiteRecordChip.FC writeStatus={returnAuthorization.returnAuthorizationNetSuiteWriteStatus} />
        },

        shippingStatus: {
          type: 'component',
          sortableValue: returnAuthorization.shipment?.status ?? 'pending',
          label: <ShippingStatusChip.FC shippingStatus={returnAuthorization.shipment?.status ?? 'pending'} />
        },
        itemsReceivedCount: {
          type: 'string',
          sortableValue: returnAuthorization.itemsReceivedCount,
          label: returnAuthorization.itemsReceivedCount.toString()
        }
      }
    })),
    [
      queryTuple.data.returnAuthorizations,
      loading,
      handleClickAccept,
      handleClickApproveDeny,
      handleClickReceiveItems,
      handleClickShowLabel,
      handleClickGenerateLabel
    ]
  );

  const columns = tableUtil.useSortedColumns({
    columnIdsToRender: props.columnIdsToRender,
    columns: __columns
  });

  return (
    <>
      <TableComponent
        rows={rows}
        columns={columns}
        disableClientSorting
        initSortOptions={initSortOptions}
        onChangeSortOptions={handleChangeSortOptions}
        paginationOptions={Table.pageInfo2PaginationOptions(pageInfo, fnQueryPage)} />

      {modalType2TargetId.receiveItems && (
        <ReceiveReturnAuthorizationModal.FC
          storeId={props.filter?.storeId || undefined}
          returnAuthorizationId={modalType2TargetId.receiveItems}
          onClose={() => setModalIsOpen('receiveItems', false)}
          onReceiveItems={() => queryTuple.refetch?.()} />
      )}

      {modalType2TargetId.approveReturnAuthorization && (
        <ApproveReturnAuthorizationModal.FC
          storeId={props.filter?.storeId || undefined}
          returnAuthorizationId={modalType2TargetId.approveReturnAuthorization}
          onClose={() => setModalIsOpen('approveReturnAuthorization', false)}
          onUpdateApprovalStatus={() => queryTuple.refetch?.()} />
      )}

      {modalType2TargetId.acceptReturnAuthorization && (
        <AcceptReturnAuthorizationModal.FC
          storeId={props.filter?.storeId || undefined}
          returnAuthorizationId={modalType2TargetId.acceptReturnAuthorization}
          onClose={() => setModalIsOpen('acceptReturnAuthorization', false)}
          onUpdateAcceptanceStatus={() => queryTuple.refetch?.()} />
      )}

      <LoadingOverlay.FC active={loading} />
    </>
  );
};



function __formatDate(dateTime?: DateTime) {
  return dateTime?.toFormat('M-d-yy') ?? '-';
}



const __columns: Table.IColumn<types.IColumnId>[] = [
  {
    id: 'requestDate',
    label: "Requested Date"
  },
  {
    id: 'approvalDate',
    label: "Approved Date"
  },
  {
    id: 'orderNumber',
    label: "Order Number"
  },
  {
    id: 'orderDate',
    label: "Order Date"
  },
  {
    id: 'orderAmount',
    label: "Order Amount"
  },
  {
    id: 'storeName',
    label: "Store"
  },
  {
    id: 'orderExternalId',
    label: "Order External ID",
    disableSorting: true
  },
  {
    id: 'netSuiteSalesOrderRecord',
    label: "NS Record",
    disableSorting: true
  },
  {
    id: 'netSuiteReturnAuthorizationRecord',
    label: "NS RMA",
    disableSorting: true
  },
  {
    id: 'itemsToBeReturnedCount',
    label: "Items to be Returned",
    disableSorting: true
  },
  {
    id: 'acceptDenyRequest',
    label: "Accept/Deny Return",
    disableSorting: true
  },
  {
    id: 'customerPhoneNumber',
    label: "Phone Number"
  },
  {
    id: 'itemsFulfilledCount',
    label: "Items Fulfilled",
    disableSorting: true
  },
  {
    id: 'customerEmail',
    label: "Email"
  },
  {
    id: 'customerFullName',
    label: "Customer Name"
  },
  {
    id: 'shippingLabel',
    label: "Shipping Label",
    disableSorting: true
  },
  {
    id: 'receiveItems',
    label: "Receive Items",
    disableSorting: true
  },
  {
    id: 'returnNumber',
    label: "RMA NUMBER"
  },
  {
    id: 'shippingStatus',
    label: "Shipping Status",
    disableSorting: true
  },
  {
    id: 'approveDenyRequest',
    label: "Approve/Deny Request",
    disableSorting: true
  },
  {
    id: 'itemsReceivedCount',
    label: "Items Received",
    disableSorting: true
  }
];
